import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Shield, AlertTriangle, Activity, Globe, Clock, Server, Users, Lock, Search, Filter, MoreVertical, RefreshCcw, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const SecurityDashboard = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLive, setIsLive] = useState(true); // Keep live updates, but we'll fetch for initial load
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [activeThreatData, setActiveThreatData] = useState([]); // Initialize as an empty array
  const [totalThreats, setTotalThreats] = useState(0); // Total number of threats (from server)
  const [pageCount, setPageCount] = useState(1); // Calculate page count from the server data
  const [loading, setLoading] = useState(true);  // Loading state
  const itemsPerPage = 5;  // This might still be used for initial request or handled on the server-side.

    // --- API Endpoint (Replace with your actual endpoint) ---
  const API_ENDPOINT = '/api/threats';

  // --- Build the request body ---
  const buildRequestBody = () => {
    return {
      timeRange: selectedTimeRange,
      severity: selectedSeverity,
      search: searchQuery,
      page: currentPage,
      pageSize: itemsPerPage,
    };
  };

  // --- Fetch Threats Function (Handles Server-Side Interactions) ---
  const fetchThreats = async () => {
    setLoading(true);

    try {
      const requestBody = buildRequestBody();
      const response = await fetch(API_ENDPOINT, {  // No query params in URL
        method: 'POST',  // Use POST
        headers: {
          'Content-Type': 'application/json',  // Specify JSON content
        },
        body: JSON.stringify(requestBody),  // Send the body as JSON
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setActiveThreatData(data.threats);
      setTotalThreats(data.total);
      setPageCount(Math.ceil(data.total / itemsPerPage));

    } catch (error) {
      console.error('Error fetching threats:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---  useEffect Hook (for initial load and when filters change) ---
  useEffect(() => {
    fetchThreats();
  }, [selectedTimeRange, selectedSeverity, searchQuery, currentPage]);

  // --- Simulated Real-time Updates (Optional - if you want live updates)---
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        fetchThreats();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [isLive, fetchThreats]);

  // Threat metrics calculation (now uses data from the server)
  const metrics = {
    total: totalThreats,  // Use total from server.
    critical: activeThreatData.filter(t => t.severity === 'critical').length,
    high: activeThreatData.filter(t => t.severity === 'high').length,
    medium: activeThreatData.filter(t => t.severity === 'medium').length,
    low: activeThreatData.filter(t => t.severity === 'low').length,
    blocked: activeThreatData.filter(t => t.blocked).length,
    mitigated: activeThreatData.filter(t => t.mitigated).length,
  };

  const SEVERITY_COLORS = {
    critical: '#DC2626',
    high: '#EA580C',
    medium: '#D97706',
    low: '#2563EB'
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      {/* Header with controls */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold">Security Operations Center</h1>
          <div className="flex items-center bg-gray-800 rounded-lg px-3 py-1">
            <Clock size={16} className="mr-2" />
            <select
              className="bg-transparent border-none focus:ring-0"
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <button
            className={`px-3 py-1 rounded-lg ${isLive ? 'bg-green-600' : 'bg-gray-700'}`}
            onClick={() => setIsLive(!isLive)}
          >
            <div className="flex items-center">
              <RefreshCcw size={16} className="mr-2" />
              Live Feed
            </div>
          </button>
          <button className="px-3 py-1 bg-gray-700 rounded-lg">
            <div className="flex items-center">
              <Download size={16} className="mr-2" />
              Export
            </div>
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-400">Total Threats</p>
              <h3 className="text-2xl font-bold">{metrics.total}</h3>
            </div>
            <Shield className="text-blue-400" size={24} />
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Active</span>
              <span className="text-blue-400">{metrics.total - metrics.mitigated}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-400">Critical Severity</p>
              <h3 className="text-2xl font-bold text-red-500">{metrics.high}</h3>
            </div>
            <AlertTriangle className="text-red-500" size={24} />
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">High Severity</span>
              <span className="text-orange-500">{metrics.high}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-400">Blocked Attacks</p>
              <h3 className="text-2xl font-bold text-green-500">{metrics.blocked}</h3>
            </div>
            <Lock className="text-green-500" size={24} />
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Success Rate</span>
              <span className="text-green-500">
                {((metrics.blocked / metrics.total) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-400">Unique Sources</p>
              <h3 className="text-2xl font-bold text-purple-500">
                {new Set(activeThreatData.map(t => t.source_ip)).size}
              </h3>
            </div>
            <Globe className="text-purple-500" size={24} />
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Countries</span>
              <span className="text-purple-500">
                {new Set(activeThreatData.map(t => t.geo_location)).size}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section (Uses `activeThreatData`) */}   
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Threat Distribution</h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(activeThreatData.reduce((acc, threat) => {
                      acc[threat.threat_type] = (acc[threat.threat_type] || 0) + 1;
                      return acc;
                    }, {})).map(([name, value]) => ({ name, value }))} // Convert to the format PieChart expects
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#3B82F6"
                    labelLine={false} // Hide the lines connecting slices to labels
                  >
                    {activeThreatData.length > 0 && Object.entries(activeThreatData.reduce((acc, threat) => {
                      acc[threat.threat_type] = (acc[threat.threat_type] || 0) + 1;
                      return acc;
                    }, {})).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SEVERITY_COLORS[Object.keys(SEVERITY_COLORS)[index % Object.keys(SEVERITY_COLORS).length]]}  />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#374151', border: 'none', borderRadius: '0.375rem' }} // Darker background, rounded corners
                    labelStyle={{ color: '#F3F4F6' }} // White label color
                    itemStyle={{ color: '#F3F4F6' }} // White item color
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
  

        <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Attack Trend</h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activeThreatData.slice(-20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    stroke="#9CA3AF"
                    tickFormatter={(time) => new Date(time).toLocaleTimeString()}
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    labelStyle={{ color: '#F3F4F6' }}
                    labelFormatter={(label) => new Date(label).toLocaleString()}
                  />
                  <Line
                    type="monotone"
                    dataKey="risk_score"
                    stroke="#3B82F6"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Threat Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Active Threats</h2>
          <div className="flex space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search threats..."
                className="bg-gray-700 rounded-lg pl-10 pr-4 py-2 w-64 focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to the first page when search changes
                }}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            </div>
            <select
              className="bg-gray-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              value={selectedSeverity}
              onChange={(e) => {
                setSelectedSeverity(e.target.value);
                setCurrentPage(1); // Reset to the first page when filter changes
              }}
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p>Loading threats...</p> // Show loading message
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="p-3 text-left"></th>
                  <th className="p-3 text-left">Timestamp</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Severity</th>
                  <th className="p-3 text-left">Source IP</th>
                  <th className="p-3 text-left">Browser</th>
                  <th className="p-3 text-left">OS</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeThreatData.map((threat, index) => (
                  <tr
                    key={threat.id}
                    className="border-b border-gray-700 hover:bg-gray-700"
                    onClick={() => setSelectedThreat(threat)}
                  >
                    <td className="p-3">{index + 1}</td>
                    <td className="p-3">{new Date(threat.timestamp).toLocaleString()}</td>
                    <td className="p-3">{threat.threat_type}</td>
                    <td className={`p-3 font-medium ${
                      threat.severity === 'critical' ? 'text-red-500' :
                        threat.severity === 'high' ? 'text-orange-500' :
                          threat.severity === 'medium' ? 'text-yellow-500' :
                            'text-blue-500'
                    }`}>{threat.severity.toUpperCase()}</td>
                    <td className="p-3">{threat.source_ip}</td>
                    <td className="p-3">{threat.browser_name}</td>
                    <td className="p-3">{threat.operating_system}</td>
                    <td className={`p-3 ${threat.blocked ? 'text-red-500' : 'text-green-500'}`}>{threat.blocked ? 'Blocked' : 'Active'}</td>
                    <td className="p-3">
                      <MoreVertical size={20} className="cursor-pointer" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}


        {/* Pagination */}
        {!loading && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-400">
              Showing {activeThreatData.length} of {totalThreats} threats
            </div>
            <div className="flex items-center space-x-2">
              <button
                className={`px-3 py-1 rounded-lg bg-gray-700 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-gray-400">{currentPage} of {pageCount}</span>
              <button
                className={`px-3 py-1 rounded-lg bg-gray-700 ${currentPage === pageCount ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
                disabled={currentPage === pageCount}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityDashboard;